import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type {
  EditData,
  GenerationItem,
  GenerationProgress,
  GenerationResults,
  PlaceKeywordSettings,
} from '@/types';
import { keywordService } from '@/services';
import { EditGateStep } from './EditGateStep';
import { RateLimitAlert } from './RateLimitAlert';

interface KeywordGenerationModalProps {
  open: boolean;
  placeId: number;
  onClose: () => void;
  onComplete: () => void;
}

type ModalState = 'options' | 'generating' | 'edit_gate' | 'results' | 'saving' | 'partial_completed';

export function KeywordGenerationModal({
  open,
  placeId,
  onClose,
  onComplete,
}: KeywordGenerationModalProps) {
  const [state, setState] = useState<ModalState>('options');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_settings, setSettings] = useState<PlaceKeywordSettings | null>(null);
   const [maxCount, setMaxCount] = useState(100);
   const [rankThreshold, setRankThreshold] = useState(10);
   const [runId, setRunId] = useState<number | null>(null);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [results, setResults] = useState<GenerationResults | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState<EditData | null>(null);

  useEffect(() => {
    if (open && placeId) {
      checkActiveGeneration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placeId]);

  const checkActiveGeneration = async () => {
    try {
      const activeRun = await keywordService.getActiveGeneration(placeId);
      if (activeRun && activeRun.run_id) {
        setRunId(activeRun.run_id);
        setProgress(activeRun);
        setState('generating');
      } else {
        loadSettings();
      }
    } catch {
      loadSettings();
    }
  };

  useEffect(() => {
    if (state !== 'generating' || !runId) return;

    const interval = setInterval(async () => {
      try {
        const status = await keywordService.getGenerationProgress(placeId, runId);
        setProgress(status);

        if (status.status === 'awaiting_user_edit') {
          const data = await keywordService.getEditData(placeId, runId);
          setEditData(data);
          setState('edit_gate');
          clearInterval(interval);
        } else if (status.status === 'completed') {
          const generationResults = await keywordService.getGenerationResults(placeId, runId);
          setResults(generationResults);
          setSelectedIds(new Set(generationResults.items.map(item => item.id)));
          setState('results');
          clearInterval(interval);
        } else if (status.status === 'partial_completed') {
          const generationResults = await keywordService.getGenerationResults(placeId, runId);
          setResults(generationResults);
          setState('partial_completed');
          clearInterval(interval);
        } else if (status.status === 'failed') {
          setError(status.error_message || '키워드 생성에 실패했습니다');
          clearInterval(interval);
        }
      } catch {
        setError('진행률 조회에 실패했습니다');
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [state, runId, placeId]);

   const loadSettings = async () => {
     try {
       const loadedSettings = await keywordService.getKeywordSettings(placeId);
       setSettings(loadedSettings);
       setRankThreshold(loadedSettings.rank_threshold);
     } catch {
       setError('설정을 불러오는데 실패했습니다');
     }
   };

  const handleStart = async () => {
    setIsLoading(true);
    setError(null);
    try {
       const response = await keywordService.startGeneration(placeId, {
         max_count: maxCount,
         rank_threshold: rankThreshold,
       });
      setRunId(response.run_id);
      setState('generating');
    } catch (error: unknown) {
      // Axios 에러에서 상세 메시지 추출
      const axiosError = error as { response?: { status?: number; data?: { detail?: string } } };
      
      if (axiosError.response?.status === 409) {
        setError('이미 진행 중인 키워드 생성이 있습니다. 완료될 때까지 기다려주세요.');
      } else if (axiosError.response?.data?.detail) {
        setError(axiosError.response.data.detail);
      } else {
        setError('키워드 생성 시작에 실패했습니다');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleItem = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleToggleAll = () => {
    if (results) {
      if (selectedIds.size === results.items.length) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(results.items.map(item => item.id)));
      }
    }
  };

  const handleSave = async () => {
    if (!runId || selectedIds.size === 0) return;

    setState('saving');
    setIsLoading(true);
    try {
      await keywordService.saveGeneratedKeywords(placeId, runId, {
        item_ids: Array.from(selectedIds),
      });
      onComplete();
      onClose();
    } catch {
      setError('키워드 저장에 실패했습니다');
      setState('results');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmEdit = async (data: EditData) => {
    if (!runId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      await keywordService.confirmEditData(placeId, runId, data);
      setState('generating');
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || '편집 확인에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyPartial = async () => {
    if (!runId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      await keywordService.applyPartialResults(placeId, runId);
      onComplete();
      onClose();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || '부분 결과 저장에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRollbackPartial = async () => {
    if (!runId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      await keywordService.rollbackPartialResults(placeId, runId);
      onClose();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || '롤백에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('키워드 생성을 취소하시겠습니까?')) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await keywordService.cancelActiveGeneration(placeId);
      setState('options');
      setRunId(null);
      setProgress(null);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || '취소에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

   const handleClose = () => {
     if (state === 'generating') {
       return;
     }
    setState('options');
    setRunId(null);
    setProgress(null);
    setResults(null);
    setSelectedIds(new Set());
    setError(null);
    setEditData(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        키워드 자동 생성
        <IconButton onClick={handleClose} disabled={state === 'generating'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {state === 'options' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography gutterBottom>생성 키워드 수: {maxCount}개</Typography>
              <Slider
                value={maxCount}
                onChange={(_, value) => setMaxCount(value as number)}
                min={10}
                max={500}
                step={10}
                marks={[
                  { value: 10, label: '10' },
                  { value: 100, label: '100' },
                  { value: 250, label: '250' },
                  { value: 500, label: '500' },
                ]}
              />
            </Box>

            <Box>
              <Typography gutterBottom>기준 등수: {rankThreshold}위 이내</Typography>
              <Slider
                value={rankThreshold}
                onChange={(_, value) => setRankThreshold(value as number)}
                min={1}
                max={20}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                ]}
              />
            </Box>
          </Box>
        )}

        {state === 'edit_gate' && editData && (
          <EditGateStep
            editData={editData}
            onConfirm={handleConfirmEdit}
            onCancel={handleClose}
          />
        )}

        {state === 'generating' && progress && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {progress.message || '키워드 생성 중...'}
            </Typography>
            <Box sx={{ px: 4 }}>
              <LinearProgress variant="determinate" value={progress.progress} sx={{ height: 10, borderRadius: 5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {progress.progress}% 완료
              </Typography>
            </Box>
            {progress.rateLimitInfo && (
              <Box sx={{ px: 4 }}>
                <RateLimitAlert rateLimitInfo={progress.rateLimitInfo} />
              </Box>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">생성됨</Typography>
                <Typography variant="h6">{progress.stats.total_generated}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">필터링</Typography>
                <Typography variant="h6">{progress.stats.total_filtered}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">유효</Typography>
                <Typography variant="h6">{progress.stats.total_ranked}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {state === 'partial_completed' && results && progress && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ 키워드 생성이 중단되었습니다
              </Typography>
              <Typography variant="body2">
                {progress.error_message || '에러가 발생했습니다'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                진행률: {progress.progress}% ({results.items.length}개 키워드 생성됨)
              </Typography>
            </Alert>

            <Typography variant="subtitle2" gutterBottom>
              생성된 키워드 미리보기:
            </Typography>

            <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>키워드</TableCell>
                    <TableCell align="right">순위</TableCell>
                    <TableCell>지도</TableCell>
                    <TableCell>출처</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.items.slice(0, 10).map((item: GenerationItem) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.keyword}</TableCell>
                      <TableCell align="right">{item.rank ? `${item.rank}위` : '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.map_type === 'new' ? '신지도' : '구지도'} 
                          size="small" 
                          color={item.map_type === 'new' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={item.source || 'generated'} size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {results.items.length > 10 && (
              <Typography variant="body2" color="text.secondary" align="center">
                전체 {results.items.length}개 중 10개 표시
              </Typography>
            )}
          </Box>
        )}

        {state === 'results' && results && (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip label={`총 ${results.items.length}개`} />
                <Chip label={`신지도 ${results.summary.new_map_count}개`} color="primary" variant="outlined" />
                <Chip label={`구지도 ${results.summary.old_map_count}개`} color="secondary" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {selectedIds.size}개 선택됨
              </Typography>
            </Box>

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.size === results.items.length}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < results.items.length}
                        onChange={handleToggleAll}
                      />
                    </TableCell>
                    <TableCell>키워드</TableCell>
                    <TableCell>유형</TableCell>
                    <TableCell align="right">순위</TableCell>
                    <TableCell>지도</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.items.map((item: GenerationItem) => (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => handleToggleItem(item.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedIds.has(item.id)} />
                      </TableCell>
                      <TableCell>{item.keyword}</TableCell>
                      <TableCell>
                        <Chip label={item.keyword_type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            color: (item.rank || 999) <= 5 ? 'success.main' : 'text.primary',
                          }}
                        >
                          {item.rank ? `${item.rank}위` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.map_type === 'new_map' ? '신지도' : '구지도'}
                          size="small"
                          color={item.map_type === 'new_map' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {state === 'saving' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2 }}>키워드 저장 중...</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {state === 'options' && (
          <>
            <Button onClick={handleClose}>취소</Button>
            <Button variant="contained" onClick={handleStart} disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} /> : '생성 시작'}
            </Button>
          </>
        )}
        {state === 'generating' && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : '취소'}
          </Button>
        )}
        {state === 'results' && (
          <>
            <Button onClick={handleClose}>닫기</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={selectedIds.size === 0 || isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : `${selectedIds.size}개 저장`}
            </Button>
          </>
        )}
        {state === 'partial_completed' && (
          <>
            <Button onClick={handleRollbackPartial} disabled={isLoading}>
              롤백하고 닫기
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyPartial}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : `${results?.items.length || 0}개 키워드 저장하기`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
